import { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import {
  Plus,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit,
  Eye,
  Ear,
  Hand,
  Image,
  FileText,
  Calendar,
  Timer,
  Info,
  ChevronDown,
  Sunrise,
  Sun,
  Sunset,
} from "lucide-react";
import projectAssetService, {
  ProjectAsset,
  ProjectAssetCheckpoint,
} from "../../../services/projectAssetService";
import { checkpointService } from "../../../services/checkpointService";
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { getDefaultBufferTime, getBufferTimeOptions, formatBufferTime, getMaxBufferTime } from '../../project/utils/projectAssetCheckpointUtils';
import { SchedulingConfig, getDefaultSchedulingConfig } from '../../project/utils/advancedSchedulingUtils';

interface ProjectAssetDrawerProps {
  open: boolean;
  onClose: () => void;
  assetName: string;
  projectId: string;
  masterAssetId?: string;
  projectAssetId?: string; // Optional: Direct project asset ID
  projectStartDate?: string;
  projectEndDate?: string;
  projectRequirement?: 'known' | 'unknown'; // AMS project requirement
}

const FREQUENCY_OPTIONS = [
  "Daily",
  "Weekly",
  "Fortnightly",
  "Monthly",
  "Quarterly",
  "Half-Yearly",
  "Yearly",
];

const FREQUENCY_COLORS: Record<string, string> = {
  Daily: "border-green-500 text-green-600",
  Weekly: "border-blue-500 text-blue-600",
  Fortnightly: "border-indigo-500 text-indigo-600",
  Monthly: "border-purple-500 text-purple-600",
  Quarterly: "border-pink-500 text-pink-600",
  "Half-Yearly": "border-orange-500 text-orange-600",
  Yearly: "border-teal-500 text-teal-600",
};

const FREQUENCY_STROKE_COLORS: Record<string, string> = {
  Daily: "border-green-200 bg-green-50",
  Weekly: "border-blue-200 bg-blue-50",
  Fortnightly: "border-indigo-200 bg-indigo-50",
  Monthly: "border-purple-200 bg-purple-50",
  Quarterly: "border-pink-200 bg-pink-50",
  "Half-Yearly": "border-orange-200 bg-orange-50",
  Yearly: "border-teal-200 bg-teal-50",
};

const formatRelativeTime = (dateString: string | Date) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

export default function AmsProjectAssetDrawer({
  open,
  onClose,
  assetName,
  projectId,
  masterAssetId,
  projectAssetId,
  projectStartDate,
  projectEndDate,
  projectRequirement = 'known', // Default to 'known' for backward compatibility
}: ProjectAssetDrawerProps) {
  const requirement = projectRequirement || 'known';
  const [loading, setLoading] = useState(false);
  const [projectAsset, setProjectAsset] = useState<ProjectAsset | null>(null);
  const [checkpoints, setCheckpoints] = useState<ProjectAssetCheckpoint[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [allCheckpoints, setAllCheckpoints] = useState<ProjectAssetCheckpoint[]>([]);

  // Master Asset Checkpoints
  const [masterCheckpoints, setMasterCheckpoints] = useState<any[]>([]);
  const [loadingMasterCheckpoints, setLoadingMasterCheckpoints] = useState(false);
  const [showMasterCheckpoints, setShowMasterCheckpoints] = useState(false);

  const [newCheckpoint, setNewCheckpoint] = useState({
    name: "",
    frequency: "Monthly" as const,
    bufferTimeDays: getDefaultBufferTime('Monthly'),
    requirement: "",
    description: "",
    taskType: [] as ('Visual' | 'Hearing' | 'Manual')[],
    isImageRequired: false,
    isInputRequired: false,
    executionPhase: "",
    executionTime: "",
    schedulingConfig: getDefaultSchedulingConfig(),
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingCheckpoint, setEditingCheckpoint] = useState<string | null>(null);

  // Edit form state
  const [editForm, setEditForm] = useState<{
    name: string;
    frequency: string;
    bufferTimeDays: number;
    requirement: string;
    description: string;
    taskType: ('Visual' | 'Hearing' | 'Manual')[];
    isImageRequired: boolean;
    isInputRequired: boolean;
    executionPhase: string;
    executionTime: string;
    schedulingConfig: SchedulingConfig;
  } | null>(null);

  // New Asset Creation Mode State
  const [isNewAssetMode, setIsNewAssetMode] = useState(false);
  const [selectedMasterCheckpointIds, setSelectedMasterCheckpointIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && projectId && assetName) {
      if (isNewAssetMode) {
        // If in creation mode, just fetch master checkpoints
        if (masterAssetId) fetchMasterCheckpoints();
      } else {
        fetchProjectAssetData();
      }
    }
  }, [open, projectId, assetName]);


  // Fetch master checkpoints when projectAsset is loaded and has masterAssetId, OR in new asset mode
  useEffect(() => {
    if (open && (isNewAssetMode || (projectAsset && (projectAsset.masterAssetId || masterAssetId)))) {
      fetchMasterCheckpoints();
    }
  }, [open, projectAsset, masterAssetId, isNewAssetMode]);

  const groupedCheckpoints = useMemo(() => {
    const groups: Record<string, ProjectAssetCheckpoint[]> = {};
    FREQUENCY_OPTIONS.forEach((freq) => {
      const filteredCheckpoints = showInactive
        ? checkpoints.filter((cp) => cp.frequency === freq)
        : checkpoints.filter((cp) => cp.frequency === freq && cp.status === "active");
      groups[freq] = filteredCheckpoints;
    });
    return groups;
  }, [checkpoints, showInactive]);

  const fetchProjectAssetData = async () => {
    setLoading(true);
    try {
      const assetWithCheckpoints =
        await projectAssetService.getProjectAssetByProjectAndAssetName(
          projectId,
          assetName
        );
      setProjectAsset(assetWithCheckpoints);
      const checkpointsData = assetWithCheckpoints.checkpoints || [];
      setCheckpoints(checkpointsData);
      setAllCheckpoints(checkpointsData);
      setIsNewAssetMode(false);
    } catch (error: any) {
      if (error?.response?.status === 404 && masterAssetId) {
        // Instead of auto-creating, enable creation mode
        console.log('[ProjectAssetDrawer] Asset not found, enabling creation mode');
        setIsNewAssetMode(true);
        // fetchMasterCheckpoints will be triggered by useEffect
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to load asset data"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWithCheckpoints = async () => {
    if (!masterAssetId) {
      toast.error("Master Asset ID is required");
      return;
    }

    setLoading(true);
    try {
      const selectedIds = Array.from(selectedMasterCheckpointIds);

      const createdAsset = await projectAssetService.createOrGetProjectAsset(
        projectId,
        masterAssetId,
        selectedIds
      );

      toast.success(`Project asset created with ${selectedIds.length} checkpoints`);

      // Reset mode and load full data
      setIsNewAssetMode(false);

      // Need to reload to get the full formatted object with checkpoints
      const assetWithCheckpoints =
        await projectAssetService.getProjectAssetWithCheckpoints(
          createdAsset._id
        );
      setProjectAsset(assetWithCheckpoints);
      const checkpointsData = assetWithCheckpoints.checkpoints || [];
      setCheckpoints(checkpointsData);
      setAllCheckpoints(checkpointsData);

    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to create project asset");
    } finally {
      setLoading(false);
    }
  };

  // Helper to toggle master checkpoint selection
  const toggleMasterCheckpointSelection = (id: string) => {
    const newSet = new Set(selectedMasterCheckpointIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedMasterCheckpointIds(newSet);
  };

  const toggleAllMasterCheckpoints = () => {
    if (selectedMasterCheckpointIds.size === masterCheckpoints.length) {
      setSelectedMasterCheckpointIds(new Set());
    } else {
      const allIds = new Set(masterCheckpoints.map(cp => cp._id || cp.id));
      setSelectedMasterCheckpointIds(allIds);
    }
  };

  // Fetch Master Asset Checkpoints
  const fetchMasterCheckpoints = async () => {
    // Check if we have any valid ID to use
    const idToUse = (projectAsset?.masterAssetId as any)?._id || projectAsset?.masterAssetId || masterAssetId;

    if (!idToUse) {
      console.log('[ProjectAssetDrawer] No master asset ID available');
      return;
    }

    setLoadingMasterCheckpoints(true);
    try {
      // Use the masterAssetId from projectAsset if available, otherwise use prop
      const assetId = (projectAsset?.masterAssetId as any)?._id || projectAsset?.masterAssetId || masterAssetId;

      console.log('[ProjectAssetDrawer] Fetching master checkpoints for asset:', assetId);

      // Fetch checkpoints from master asset
      const response = await checkpointService.getCheckpoints({
        assetId: assetId,
        status: 'active',
        limit: 1000
      });


      // Access nested data structure correctly
      // Response is CheckpointListResponse { data: { checkpoints: [] } }
      const responseData = response.data as any;
      const masterCps = responseData?.checkpoints || responseData || [];

      console.log('[ProjectAssetDrawer] Loaded master checkpoints:', masterCps.length);
      setMasterCheckpoints(Array.isArray(masterCps) ? masterCps : []);

      // If in creation mode, select all by default? Or none? Let's select all by default as it's easier
      if (isNewAssetMode && masterCps.length > 0) {
        const allIds = new Set(masterCps.map((cp: any) => cp._id || cp.id));
        setSelectedMasterCheckpointIds(allIds as Set<string>);
      }

      if (masterCps.length === 0) {
        toast.info('No checkpoints found in master asset');
      }
    } catch (error: any) {
      console.error('Failed to fetch master checkpoints:', error);
      toast.error(error?.response?.data?.message || 'Failed to load master checkpoints');
    } finally {
      setLoadingMasterCheckpoints(false);
    }
  };

  // Import checkpoint from master asset
  const importFromMaster = async (masterCheckpoint: any) => {
    if (!projectAsset?._id) {
      toast.error("Project asset not found");
      return;
    }

    try {
      // Check if checkpoint with same name already exists
      const existingCheckpoint = checkpoints.find(
        cp => cp.name.toLowerCase() === masterCheckpoint.name.toLowerCase()
      );

      if (existingCheckpoint) {
        toast.warning(`Checkpoint "${masterCheckpoint.name}" already exists`);
        return;
      }

      // Create checkpoint from master
      const createdCheckpoint = await projectAssetService.createProjectAssetCheckpoint(
        projectAsset._id,
        {
          name: masterCheckpoint.name,
          frequency: masterCheckpoint.frequency,
          requirement: masterCheckpoint.requirement,
          bufferTimeDays: masterCheckpoint.bufferTimeDays,
          description: masterCheckpoint.description,
          taskType: masterCheckpoint.taskType,
          isImageRequired: masterCheckpoint.isImageRequired,
          isInputRequired: masterCheckpoint.isInputRequired,
          executionPhase: masterCheckpoint.executionPhase,
          executionTime: masterCheckpoint.executionTime,
          schedulingConfig: masterCheckpoint.schedulingConfig || getDefaultSchedulingConfig(),
        }
      );

      const updatedCheckpoints = [...checkpoints, createdCheckpoint];
      setCheckpoints(updatedCheckpoints);
      setAllCheckpoints(updatedCheckpoints);

      toast.success(`Imported checkpoint "${masterCheckpoint.name}" from master asset`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to import checkpoint');
    }
  };

  // Import all checkpoints from master asset
  const importAllFromMaster = async () => {
    if (!projectAsset?._id || masterCheckpoints.length === 0) {
      toast.error("No master checkpoints to import");
      return;
    }

    const confirmImport = window.confirm(
      `This will import ${masterCheckpoints.length} checkpoints from master asset. Existing checkpoints will be skipped. Continue?`
    );

    if (!confirmImport) return;

    let importedCount = 0;
    let skippedCount = 0;

    setIsLoading(true);
    try {
      for (const masterCheckpoint of masterCheckpoints) {
        // Check if checkpoint with same name already exists
        const existingCheckpoint = checkpoints.find(
          cp => cp.name.toLowerCase() === masterCheckpoint.name.toLowerCase()
        );

        if (existingCheckpoint) {
          skippedCount++;
          continue;
        }

        // Create checkpoint from master
        await projectAssetService.createProjectAssetCheckpoint(
          projectAsset._id,
          {
            name: masterCheckpoint.name,
            frequency: masterCheckpoint.frequency,
            requirement: masterCheckpoint.requirement,
            bufferTimeDays: masterCheckpoint.bufferTimeDays,
            description: masterCheckpoint.description,
            taskType: masterCheckpoint.taskType,
            isImageRequired: masterCheckpoint.isImageRequired,
            isInputRequired: masterCheckpoint.isInputRequired,
            executionPhase: masterCheckpoint.executionPhase,
            executionTime: masterCheckpoint.executionTime,
            schedulingConfig: masterCheckpoint.schedulingConfig || getDefaultSchedulingConfig(),
          }
        );
        importedCount++;
      }

      // Reload checkpoints
      await fetchProjectAssetData();

      toast.success(
        `Imported ${importedCount} checkpoint(s). ${skippedCount > 0 ? `Skipped ${skippedCount} existing checkpoint(s).` : ''}`
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to import checkpoints');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCheckpoint = async () => {
    if (!newCheckpoint.name.trim()) {
      toast.error("Checkpoint name is required");
      return;
    }
    if (!projectAsset?._id) {
      toast.error("Project asset not found");
      return;
    }

    try {
      const createdCheckpoint =
        await projectAssetService.createProjectAssetCheckpoint(
          projectAsset._id,
          {
            name: newCheckpoint.name,
            frequency: newCheckpoint.frequency,
            requirement: newCheckpoint.requirement || undefined,
            bufferTimeDays: newCheckpoint.bufferTimeDays,
            description: newCheckpoint.description || undefined,
            taskType: newCheckpoint.taskType.length > 0 ? newCheckpoint.taskType : undefined,
            isImageRequired: newCheckpoint.isImageRequired,
            isInputRequired: newCheckpoint.isInputRequired,
            executionPhase: newCheckpoint.executionPhase || undefined,
            executionTime: newCheckpoint.executionTime || undefined,
            schedulingConfig: newCheckpoint.schedulingConfig,
          }
        );
      const updatedCheckpoints = [...checkpoints, createdCheckpoint];
      setCheckpoints(updatedCheckpoints);
      setAllCheckpoints(updatedCheckpoints);
      setNewCheckpoint({
        name: "",
        frequency: "Monthly",
        bufferTimeDays: getDefaultBufferTime('Monthly'),
        requirement: "",
        description: "",
        taskType: [],
        isImageRequired: false,
        isInputRequired: false,
        executionPhase: "",
        executionTime: "",
        schedulingConfig: getDefaultSchedulingConfig()
      });
      setIsCreating(false);
      toast.success("Checkpoint added successfully");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to save checkpoint"
      );
    }
  };

  const handleToggleCheckpointStatus = async (
    checkpointId: string,
    newStatus: "active" | "inactive"
  ) => {
    try {
      await projectAssetService.updateProjectAssetCheckpointStatus(
        checkpointId,
        newStatus
      );

      // Reload all checkpoints to get the latest data
      if (projectAsset?._id) {
        const allCheckpointsData = await projectAssetService.getAllProjectAssetCheckpoints(
          projectAsset._id
        );
        setAllCheckpoints(allCheckpointsData);

        // Update current view based on current filter state
        if (showInactive) {
          setCheckpoints(allCheckpointsData);
        } else {
          const activeCheckpoints = allCheckpointsData.filter(cp => cp.status === "active");
          setCheckpoints(activeCheckpoints);
        }
      }

      toast.success(
        `Checkpoint ${newStatus === "active" ? "activated" : "deactivated"
        } successfully`
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to update checkpoint status"
      );
    }
  };

  const handleToggleView = async (showAll: boolean) => {
    setShowInactive(showAll);

    if (showAll) {
      // Fetch all checkpoints (active and inactive) from API
      try {
        setLoading(true);
        const allCheckpointsData = await projectAssetService.getAllProjectAssetCheckpoints(
          projectAsset?._id || ""
        );
        setAllCheckpoints(allCheckpointsData);
        setCheckpoints(allCheckpointsData);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || "Failed to load all checkpoints"
        );
        // Revert the toggle if API call fails
        setShowInactive(false);
      } finally {
        setLoading(false);
      }
    } else {
      // Show only active checkpoints from the stored all checkpoints
      const activeCheckpoints = allCheckpoints.filter(cp => cp.status === "active");
      setCheckpoints(activeCheckpoints);
    }
  };

  const handleProjectAssetStatusChange = async () => {
    if (!projectAsset?._id) {
      toast.error("Project asset not found");
      return;
    }

    const newStatus = projectAsset?.status === "active" ? "inactive" : "active";

    // Show confirmation modal for deactivation
    if (newStatus === "inactive") {
      setIsConfirmOpen(true);
      return;
    }

    // For activation, proceed directly
    await updateProjectAssetStatus(newStatus);
  };

  const updateProjectAssetStatus = async (newStatus: string) => {
    if (!projectAsset?._id) return;

    setIsLoading(true);
    try {
      const updatedAsset = await projectAssetService.updateProjectAssetStatus(
        projectAsset._id,
        newStatus
      );
      setProjectAsset(updatedAsset);
      toast.success("Project asset status updated successfully");
      setIsConfirmOpen(false);
    } catch (error: any) {
      // Show specific error message for equipment validation
      if (error?.response?.status === 400) {
        toast.error(error?.response?.data?.message || "Cannot deactivate project asset with active equipment");
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to update project asset status"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCheckpoint = (checkpoint: ProjectAssetCheckpoint) => {
    const checkpointId = checkpoint._id || checkpoint.id || "";
    setEditingCheckpoint(checkpointId);

    // Initialize edit form with checkpoint data
    setEditForm({
      name: checkpoint.name,
      frequency: checkpoint.frequency,
      bufferTimeDays: checkpoint.bufferTimeDays || getDefaultBufferTime(checkpoint.frequency),
      requirement: checkpoint.requirement || "",
      description: checkpoint.description || "",
      taskType: checkpoint.taskType || [],
      isImageRequired: checkpoint.isImageRequired || false,
      isInputRequired: checkpoint.isInputRequired || false,
      executionPhase: checkpoint.executionPhase || "",
      executionTime: checkpoint.executionTime || "",
      schedulingConfig: checkpoint.schedulingConfig || getDefaultSchedulingConfig(),
    });
  };

  const handleCancelEdit = () => {
    setEditingCheckpoint(null);
    setEditForm(null);
  };

  const handleSaveEdit = async (checkpointId: string) => {
    if (!editForm) return;

    // Validate edit form
    if (!editForm.name.trim()) {
      toast.error("Checkpoint name is required");
      return;
    }

    try {
      await projectAssetService.updateProjectAssetCheckpoint(
        checkpointId,
        {
          name: editForm.name,
          frequency: editForm.frequency,
          requirement: editForm.requirement || undefined,
          bufferTimeDays: editForm.bufferTimeDays,
          description: editForm.description || undefined,
          taskType: editForm.taskType.length > 0 ? editForm.taskType : undefined,
          isImageRequired: editForm.isImageRequired,
          isInputRequired: editForm.isInputRequired,
          executionPhase: editForm.executionPhase || undefined,
          executionTime: editForm.executionTime || undefined,
          schedulingConfig: editForm.schedulingConfig,
        }
      );

      // Update local state
      const updatedCheckpoints = checkpoints.map(cp =>
        (cp._id === checkpointId || cp.id === checkpointId)
          ? {
            ...cp,
            name: editForm.name,
            frequency: editForm.frequency as "Daily" | "Weekly" | "Fortnightly" | "Monthly" | "Quarterly" | "Half-Yearly" | "Yearly",
            requirement: editForm.requirement,
            bufferTimeDays: editForm.bufferTimeDays,
            description: editForm.description,
            taskType: editForm.taskType,
            isImageRequired: editForm.isImageRequired,
            isInputRequired: editForm.isInputRequired,
            executionPhase: editForm.executionPhase,
            executionTime: editForm.executionTime,
            schedulingConfig: editForm.schedulingConfig,
          }
          : cp
      );
      setCheckpoints(updatedCheckpoints);

      const updatedAllCheckpoints = allCheckpoints.map(cp =>
        (cp._id === checkpointId || cp.id === checkpointId)
          ? {
            ...cp,
            name: editForm.name,
            frequency: editForm.frequency as "Daily" | "Weekly" | "Fortnightly" | "Monthly" | "Quarterly" | "Half-Yearly" | "Yearly",
            requirement: editForm.requirement,
            bufferTimeDays: editForm.bufferTimeDays,
            description: editForm.description,
            taskType: editForm.taskType,
            isImageRequired: editForm.isImageRequired,
            isInputRequired: editForm.isInputRequired,
            executionPhase: editForm.executionPhase,
            executionTime: editForm.executionTime,
            schedulingConfig: editForm.schedulingConfig,
          }
          : cp
      );
      setAllCheckpoints(updatedAllCheckpoints);

      setEditingCheckpoint(null);
      setEditForm(null);
      toast.success("Checkpoint updated successfully");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to update checkpoint"
      );
    }
  };

  return (
    <RightDrawer
      open={open}
      title="Asset Checkpoints"
      onClose={onClose}
      size="60"
    >
      <div className="space-y-6">
        <div>
          <p className="text-sm text-gray-600 mt-1">{assetName}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Project-level asset configuration
          </p>
        </div>

        {projectRequirement === 'unknown' ? (
          // Unknown requirement - show message only
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle size={24} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-900 mb-2">Asset & Equipment Selection Not Available</h4>
                  <p className="text-sm text-amber-700">
                    Asset and equipment selection isn’t available for this project yet.
                  </p>
                  <p className="text-sm text-amber-600 mt-2">
                    Update the project settings to enable asset and equipment management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : isNewAssetMode ? (
          // Creation Mode UI
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info size={20} className="text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900">Initialize Project Asset</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    This asset hasn't been added to the project yet. Select which checkpoints you want to include from the Master Asset template.
                  </p>
                </div>
              </div>
            </div>

            {loadingMasterCheckpoints ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                <span className="ml-3 text-gray-600">Loading master checkpoints...</span>
              </div>
            ) : (
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
                  <h3 className="font-medium text-gray-900">Select Checkpoints ({selectedMasterCheckpointIds.size}/{masterCheckpoints.length})</h3>
                  <button
                    onClick={toggleAllMasterCheckpoints}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {selectedMasterCheckpointIds.size === masterCheckpoints.length ? "Deselect All" : "Select All"}
                  </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto p-2 space-y-2">
                  {masterCheckpoints.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No master checkpoints available. You can create the asset and add custom checkpoints later.
                    </div>
                  ) : (
                    masterCheckpoints.map((mc: any) => {
                      const id = mc._id || mc.id;
                      const isSelected = selectedMasterCheckpointIds.has(id);
                      return (
                        <div
                          key={id}
                          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                          onClick={() => toggleMasterCheckpointSelection(id)}
                        >
                          <div className="pt-0.5">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => { }} // Handled by div click
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-600 w-4 h-4"
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900">{mc.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 text-[10px] font-medium rounded border ${FREQUENCY_COLORS[mc.frequency]}`}>
                                {mc.frequency}
                              </span>
                              {mc.description && (
                                <span className="text-xs text-gray-500 truncate max-w-[200px]">
                                  {mc.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="p-4 bg-gray-50 border-t">
                  <button
                    onClick={handleCreateWithCheckpoints}
                    disabled={loading}
                    className="w-full py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-70 flex justify-center items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Create Asset & Add {selectedMasterCheckpointIds.size} Checkpoints
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-green-500 rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">Loading asset data...</span>
          </div>
        ) : requirement === 'unknown' ? (
          // Unknown requirement - show message only, no asset/equipment options
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle size={24} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-900 mb-2">Asset & Equipment Selection Not Available</h4>
                  <p className="text-sm text-amber-700">
                    Asset and equipment selection isn’t available for this project yet.
                  </p>
                  <p className="text-sm text-amber-600 mt-2">
                    Update the project settings to enable asset and equipment management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Asset Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Asset Information</h4>
                <button
                  onClick={() => setIsCreating(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm font-medium"
                >
                  <Plus size={16} />
                  Add Checkpoint
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Asset Name:</span>
                  <span className="ml-2 font-medium">{assetName}</span>
                </div>


              </div>

              {/* Filter Buttons */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-3">
                  {/* Filter Buttons */}
                  <div className="inline-flex rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <button
                      onClick={() => handleToggleView(false)}
                      className={`px-4 py-1.5 text-sm font-medium transition-all ${!showInactive
                        ? "bg-green-600 text-white shadow-md"
                        : "text-gray-500 hover:bg-gray-50"
                        }`}
                    >
                      Active
                    </button>
                    <button
                      onClick={() => handleToggleView(true)}
                      className={`px-4 py-1.5 text-sm font-medium transition-all ${showInactive
                        ? "bg-blue-600 text-white shadow-md"
                        : "text-gray-500 hover:bg-gray-50"
                        }`}
                    >
                      All
                    </button>
                  </div>

                  {/* Info Text */}
                  <span className="text-xs text-gray-500">
                    Showing:{" "}
                    <span className={`font-medium ${showInactive ? "text-blue-600" : "text-green-600"}`}>
                      {showInactive ? "All Checkpoints" : "Active Checkpoints"}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Master Asset Checkpoints Section */}
            {(masterAssetId || projectAsset?.masterAssetId) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Info size={18} className="text-blue-600" />
                    <h4 className="font-semibold text-gray-900">Master Asset Checkpoints</h4>
                    {masterCheckpoints.length > 0 && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {masterCheckpoints.length} available
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowMasterCheckpoints(!showMasterCheckpoints)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                  >
                    {showMasterCheckpoints ? 'Hide' : 'Show'} Master Checkpoints
                    <ChevronDown size={16} className={`transition-transform ${showMasterCheckpoints ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-3">
                  Import checkpoints from the master asset template to this project asset
                </p>

                {showMasterCheckpoints && (
                  <>
                    {loadingMasterCheckpoints ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                        <span className="ml-3 text-sm text-gray-600">Loading master checkpoints...</span>
                      </div>
                    ) : masterCheckpoints.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 text-sm">
                        No checkpoints found in master asset
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            {masterCheckpoints.length} checkpoint(s) available for import
                          </span>
                          <button
                            onClick={importAllFromMaster}
                            disabled={isLoading}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {isLoading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Importing...
                              </>
                            ) : (
                              <>
                                <Plus size={14} />
                                Import All
                              </>
                            )}
                          </button>
                        </div>

                        <div className="max-h-64 overflow-y-auto space-y-2 bg-white rounded-lg p-3 border border-blue-100">
                          {masterCheckpoints.map((mc: any) => {
                            const alreadyExists = checkpoints.some(
                              cp => cp.name.toLowerCase() === mc.name.toLowerCase()
                            );

                            return (
                              <div
                                key={mc._id || mc.id}
                                className={`flex items-center justify-between p-2 rounded-lg border ${alreadyExists
                                  ? 'bg-gray-50 border-gray-200 opacity-60'
                                  : 'bg-white border-blue-100 hover:border-blue-300'
                                  }`}
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm text-gray-900">{mc.name}</span>
                                    {alreadyExists && (
                                      <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-[10px] font-medium rounded">
                                        EXISTS
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded border ${FREQUENCY_COLORS[mc.frequency]}`}>
                                      {mc.frequency}
                                    </span>
                                    {mc.description && (
                                      <span className="text-xs text-gray-500 truncate">
                                        {mc.description}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {!alreadyExists && (
                                  <button
                                    onClick={() => importFromMaster(mc)}
                                    className="ml-3 px-3 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded hover:bg-blue-100 transition-colors flex items-center gap-1"
                                  >
                                    <Plus size={12} />
                                    Import
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              {/* Add New Checkpoint Form */}
              {isCreating && (
                <div className="mt-4 p-4 bg-white border border-green-600 shadow-md rounded-lg relative">
                  {/* Close button - top right */}
                  <button
                    onClick={() => {
                      setIsCreating(false);
                      setNewCheckpoint({
                        name: "",
                        frequency: "Monthly",
                        bufferTimeDays: getDefaultBufferTime('Monthly'),
                        requirement: "",
                        description: "",
                        taskType: [],
                        isImageRequired: false,
                        isInputRequired: false,
                        executionPhase: "",
                        executionTime: "",
                        schedulingConfig: getDefaultSchedulingConfig()
                      });
                    }}
                    className="absolute top-3 right-3 text-gray-500 hover:text-red-500 transition-colors"
                  >
                    ✕
                  </button>

                  <div className="grid grid-cols-12 gap-3 items-start">
                    {/* Checkpoint Description (big field) */}
                    <div className="col-span-12 md:col-span-5">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Checkpoint Description
                      </label>
                      <input
                        type="text"
                        value={newCheckpoint.name}
                        onChange={(e) =>
                          setNewCheckpoint((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Enter checkpoint description..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm"
                      />
                    </div>

                    {/* Requirement field */}
                    <div className="col-span-12 md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Requirement
                      </label>
                      <input
                        type="text"
                        value={newCheckpoint.requirement}
                        onChange={(e) =>
                          setNewCheckpoint((prev) => ({
                            ...prev,
                            requirement: e.target.value,
                          }))
                        }
                        placeholder="Enter requirement details..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm"
                      />
                    </div>

                    {/* Frequency (compact dropdown) */}
                    <div className="col-span-12 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Frequency
                      </label>
                      <select
                        value={newCheckpoint.frequency}
                        onChange={(e) => {
                          const frequency = e.target.value as any;
                          const maxBuffer = getMaxBufferTime(frequency);
                          const currentBuffer = newCheckpoint.bufferTimeDays || 0;
                          // If current buffer exceeds max for new frequency, set to default
                          const newBuffer = currentBuffer > maxBuffer ? getDefaultBufferTime(frequency) : currentBuffer;
                          setNewCheckpoint((prev) => ({
                            ...prev,
                            frequency,
                            bufferTimeDays: newBuffer
                          }));
                        }}
                        className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm"
                      >
                        {FREQUENCY_OPTIONS.map((freq) => (
                          <option key={freq} value={freq}>
                            {freq}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Execution Phase (compact dropdown) */}
                    <div className="col-span-12 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Execution Phase (Days)
                      </label>
                      <select
                        value={newCheckpoint.bufferTimeDays}
                        onChange={(e) =>
                          setNewCheckpoint((prev) => ({
                            ...prev,
                            bufferTimeDays: parseInt(e.target.value),
                          }))
                        }
                        className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm"
                      >
                        {getBufferTimeOptions(newCheckpoint.frequency).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className="grid grid-cols-12 gap-3 items-start mt-3">
                    <div className="col-span-12">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-gray-500 text-xs">(Optional)</span>
                      </label>
                      <textarea
                        value={newCheckpoint.description}
                        onChange={(e) =>
                          setNewCheckpoint((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Enter checkpoint description..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm resize-y"
                      />
                    </div>
                  </div>

                  {/* Task Type and Input Required */}
                  <div className="grid grid-cols-12 gap-3 items-start mt-3">
                    {/* Task Type */}
                    <div className="col-span-12 md:col-span-6">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Task Type
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {['Visual', 'Hearing', 'Manual'].map((type) => (
                          <label key={type} className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={newCheckpoint.taskType.includes(type as any)}
                              onChange={(e) => {
                                const newTypes = e.target.checked
                                  ? [...newCheckpoint.taskType, type as any]
                                  : newCheckpoint.taskType.filter((t) => t !== type);
                                setNewCheckpoint((prev) => ({ ...prev, taskType: newTypes }));
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-600"
                            />
                            <span className="ml-2 text-sm text-gray-700">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Input Required */}
                    <div className="col-span-12 md:col-span-6">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={newCheckpoint.isInputRequired}
                          onChange={(e) =>
                            setNewCheckpoint((prev) => ({
                              ...prev,
                              isInputRequired: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300 text-green-600 focus:ring-green-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Input Required</span>
                      </label>
                    </div>
                  </div>

                  {/* Execution Shift Selector */}
                  <div className="grid grid-cols-12 gap-3 items-start mt-3">
                    <div className="col-span-12">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Execution Shift <span className="text-gray-500 text-xs">(Auto-fetches execution time)</span>
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'Morning', label: 'Pre-Gen (AM)', time: '05:00-09:00', icon: Sunrise, color: 'bg-orange-50 text-orange-600 border-orange-200' },
                          { id: 'Standard', label: 'Standard (Day)', time: '09:00-18:00', icon: Sun, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
                          { id: 'Evening', label: 'Post-Gen (PM)', time: '18:00-22:00', icon: Sunset, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
                        ].map((shift) => {
                          const isSelected = newCheckpoint.executionPhase === shift.id;
                          return (
                            <button
                              key={shift.id}
                              type="button"
                              onClick={() => {
                                setNewCheckpoint((prev) => ({
                                  ...prev,
                                  executionPhase: shift.id,
                                  executionTime: shift.time,
                                }));
                              }}
                              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm border-2 transition-all ${isSelected
                                ? `${shift.color} border-current shadow-md`
                                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              <shift.icon className="w-4 h-4" />
                              <span className="font-medium">{shift.label}</span>
                            </button>
                          );
                        })}
                      </div>
                      {newCheckpoint.executionTime && (
                        <p className="mt-2 text-xs text-gray-500">
                          Execution Time: <span className="font-medium text-gray-700">{newCheckpoint.executionTime}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Image Required */}
                  <div className="grid grid-cols-12 gap-3 items-start mt-3">
                    <div className="col-span-12">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={newCheckpoint.isImageRequired}
                          onChange={(e) =>
                            setNewCheckpoint((prev) => ({
                              ...prev,
                              isImageRequired: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300 text-green-600 focus:ring-green-600"
                        />
                        <span className="ml-2 text-sm text-gray-700">Image Required</span>
                      </label>
                    </div>
                  </div>

                  {/* Save button (styled) */}
                  <div className="grid grid-cols-12 gap-3 items-start mt-3">
                    <div className="col-span-12 flex justify-end mt-4">
                      <button
                        onClick={handleSaveCheckpoint}
                        className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 shadow-sm transition-all text-sm font-medium"
                      >
                        <Save size={16} />
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Grouped Checkpoints */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Project Asset Checkpoints
              </h4>

              {Object.entries(groupedCheckpoints).map(([freq, items]) => (
                <div
                  key={freq}
                  className={`relative border-l-4 p-4 mb-4 rounded-lg shadow-sm bg-white ${FREQUENCY_COLORS[freq] || "border-gray-300 text-gray-700"
                    }`}
                >
                  <span
                    className={`absolute -top-3 left-3 bg-white px-2 text-sm font-semibold ${FREQUENCY_COLORS[freq] || ""
                      }`}
                  >
                    {freq}
                  </span>

                  {items.length === 0 ? (
                    <div className="py-4 text-center text-gray-500 text-sm">
                      No checkpoints for {freq}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {items.map((checkpoint) => {
                        const checkpointId = checkpoint._id || checkpoint.id;
                        if (!checkpointId) return null;

                        const isEditing = editingCheckpoint === checkpointId;

                        return (
                          <div
                            key={checkpointId}
                            className={`bg-white rounded-lg border shadow-sm transition-all ${isEditing
                              ? "border-[#339274]/30 bg-[#f9fafb]"
                              : "border-gray-200 hover:bg-gray-50"
                              }`}
                          >
                            {/* Header */}
                            <div className="p-4 flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="mt-0.5">
                                  <CheckCircle
                                    size={18}
                                    className="text-green-600 flex-shrink-0"
                                  />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 leading-tight mb-2">
                                    {checkpoint.name}
                                  </h4>

                                  {/* Description */}
                                  {checkpoint.description && (
                                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                                      {checkpoint.description}
                                    </p>
                                  )}

                                  {/* Requirement */}
                                  {checkpoint.requirement && (
                                    <div className="mb-2">
                                      <div className="flex items-start gap-1.5">
                                        <FileText size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs text-gray-600">
                                          <span className="font-medium text-gray-700">Requirement:</span> {checkpoint.requirement}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Task Type Badges */}
                                  {checkpoint.taskType && Array.isArray(checkpoint.taskType) && checkpoint.taskType.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                      {checkpoint.taskType.includes('Visual') && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium">
                                          <Eye size={12} />
                                          Visual
                                        </span>
                                      )}
                                      {checkpoint.taskType.includes('Hearing') && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
                                          <Ear size={12} />
                                          Hearing
                                        </span>
                                      )}
                                      {checkpoint.taskType.includes('Manual') && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 border border-orange-200 text-xs font-medium">
                                          <Hand size={12} />
                                          Manual
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {/* Input & Image Required Badges */}
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    {/* Input Required */}
                                    {checkpoint.isInputRequired && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium">
                                        <FileText size={12} />
                                        Input Required
                                      </span>
                                    )}

                                    {/* Image Required */}
                                    {checkpoint.isImageRequired && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pink-50 text-pink-700 border border-pink-200 text-xs font-medium">
                                        <Image size={12} />
                                        Image Required
                                      </span>
                                    )}
                                  </div>

                                  {/* Execution Details */}
                                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mb-2">
                                    {/* Execution Shift/Phase */}
                                    {checkpoint.executionPhase && (
                                      <div className="flex items-center gap-1">
                                        <Calendar size={12} className="text-gray-400" />
                                        <span className="text-gray-600">
                                          <span className="font-medium text-gray-700">Shift:</span> {
                                            checkpoint.executionPhase === 'Morning' ? 'Pre-Gen (AM)' :
                                              checkpoint.executionPhase === 'Standard' ? 'Standard (Day)' :
                                                checkpoint.executionPhase === 'Evening' ? 'Post-Gen (PM)' :
                                                  checkpoint.executionPhase
                                          }
                                        </span>
                                      </div>
                                    )}

                                    {/* Execution Time */}
                                    {checkpoint.executionTime && (
                                      <div className="flex items-center gap-1">
                                        <Timer size={12} className="text-gray-400" />
                                        <span className="text-gray-600">
                                          <span className="font-medium text-gray-700">Time:</span> {checkpoint.executionTime}
                                        </span>
                                      </div>
                                    )}

                                    {/* Buffer Time */}
                                    {checkpoint.bufferTimeDays !== undefined && (
                                      <div className="flex items-center gap-1">
                                        <Info size={12} className="text-gray-400" />
                                        <span className="text-gray-600">
                                          <span className="font-medium text-gray-700">Buffer:</span> {checkpoint.bufferTimeDays === 0 ? 'No buffer' : checkpoint.bufferTimeDays === 1 ? '1 day' : `${checkpoint.bufferTimeDays} days`}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Metadata */}
                                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                    <span>
                                      Created by{" "}
                                      <span className="font-medium text-gray-700">
                                        {typeof (checkpoint as any).createdBy === "object" && (checkpoint as any).createdBy
                                          ? (checkpoint as any).createdBy.name || (checkpoint as any).createdBy.email || "N/A"
                                          : (checkpoint as any).createdByName || "N/A"}
                                      </span>
                                    </span>
                                    <span>•</span>
                                    <span>
                                      Updated by{" "}
                                      <span className="font-medium text-gray-700">
                                        {typeof (checkpoint as any).updatedBy === "object" && (checkpoint as any).updatedBy
                                          ? (checkpoint as any).updatedBy.name || (checkpoint as any).updatedBy.email || "N/A"
                                          : (checkpoint as any).updatedByName || "N/A"}
                                      </span>
                                      {(checkpoint.updatedAt || checkpoint.createdAt) && (
                                        <span className="ml-1 text-gray-400">
                                          ({formatRelativeTime(checkpoint.updatedAt || checkpoint.createdAt || '')})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2">
                                {/* Expand/Collapse Icon */}
                                <ChevronDown size={16} className="text-gray-400" />

                                {/* Edit Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditCheckpoint(checkpoint);
                                  }}
                                  className="p-1.5 rounded-md text-gray-400 hover:text-[#339274] hover:bg-[#339274]/10 transition-colors"
                                  title="Edit checkpoint"
                                >
                                  <Edit size={14} />
                                </button>
                              </div>
                            </div>

                            {/* Comprehensive Edit Form */}
                            {isEditing && editForm && (
                              <div className="border-t border-gray-100 bg-gray-50/60 p-4 rounded-b-lg" onClick={(e) => e.stopPropagation()}>
                                <div className="space-y-4">
                                  <div className="flex items-center gap-2 mb-4">
                                    <Edit size={18} className="text-[#339274]" />
                                    <h4 className="text-sm font-semibold text-gray-900">Edit Checkpoint</h4>
                                  </div>

                                  <div className="grid grid-cols-12 gap-3">
                                    {/* Checkpoint Name */}
                                    <div className="col-span-12 md:col-span-5">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Checkpoint Name
                                      </label>
                                      <input
                                        type="text"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm(prev => prev ? { ...prev, name: e.target.value } : null)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-sm"
                                      />
                                    </div>

                                    {/* Requirement */}
                                    <div className="col-span-12 md:col-span-3">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Requirement
                                      </label>
                                      <input
                                        type="text"
                                        value={editForm.requirement}
                                        onChange={(e) => setEditForm(prev => prev ? { ...prev, requirement: e.target.value } : null)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-sm"
                                      />
                                    </div>

                                    {/* Frequency */}
                                    <div className="col-span-12 md:col-span-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Frequency
                                      </label>
                                      <select
                                        value={editForm.frequency}
                                        onChange={(e) => {
                                          const frequency = e.target.value;
                                          const maxBuffer = getMaxBufferTime(frequency as any);
                                          const currentBuffer = editForm.bufferTimeDays || 0;
                                          // If current buffer exceeds max for new frequency, set to default
                                          const newBuffer = currentBuffer > maxBuffer ? getDefaultBufferTime(frequency as any) : currentBuffer;
                                          setEditForm(prev => prev ? {
                                            ...prev,
                                            frequency,
                                            bufferTimeDays: newBuffer
                                          } : null);
                                        }}
                                        className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-sm"
                                      >
                                        {FREQUENCY_OPTIONS.map((freq) => (
                                          <option key={freq} value={freq}>
                                            {freq}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {/* Execution Phase */}
                                    <div className="col-span-12 md:col-span-2">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Execution Phase (Days)
                                      </label>
                                      <select
                                        value={editForm.bufferTimeDays}
                                        onChange={(e) => setEditForm(prev => prev ? { ...prev, bufferTimeDays: parseInt(e.target.value) } : null)}
                                        className="w-full px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-sm"
                                      >
                                        {getBufferTimeOptions(editForm.frequency as any).map((option) => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>

                                  {/* Description Field */}
                                  <div className="grid grid-cols-12 gap-3 mt-3">
                                    <div className="col-span-12">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description <span className="text-gray-500 text-xs">(Optional)</span>
                                      </label>
                                      <textarea
                                        value={editForm.description}
                                        onChange={(e) => setEditForm(prev => prev ? { ...prev, description: e.target.value } : null)}
                                        placeholder="Enter checkpoint description..."
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-blue-600 text-sm resize-y"
                                      />
                                    </div>
                                  </div>

                                  {/* Task Type and Input Required */}
                                  <div className="grid grid-cols-12 gap-3 mt-3">
                                    {/* Task Type */}
                                    <div className="col-span-12 md:col-span-6">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Task Type
                                      </label>
                                      <div className="flex gap-2 flex-wrap">
                                        {['Visual', 'Hearing', 'Manual'].map((type) => (
                                          <label key={type} className="inline-flex items-center">
                                            <input
                                              type="checkbox"
                                              checked={(editForm.taskType || []).includes(type as any)}
                                              onChange={(e) => {
                                                const currentTypes = editForm.taskType || [];
                                                const newTypes = e.target.checked
                                                  ? [...currentTypes, type as any]
                                                  : currentTypes.filter((t) => t !== type);
                                                setEditForm(prev => prev ? { ...prev, taskType: newTypes } : null);
                                              }}
                                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">{type}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Input Required */}
                                    <div className="col-span-12 md:col-span-6">
                                      <label className="inline-flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={editForm.isInputRequired || false}
                                          onChange={(e) => setEditForm(prev => prev ? { ...prev, isInputRequired: e.target.checked } : null)}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Input Required</span>
                                      </label>
                                    </div>
                                  </div>

                                  {/* Execution Shift Selector */}
                                  <div className="grid grid-cols-12 gap-3 mt-3">
                                    <div className="col-span-12">
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Execution Shift <span className="text-gray-500 text-xs">(Auto-fetches execution time)</span>
                                      </label>
                                      <div className="grid grid-cols-3 gap-2">
                                        {[
                                          { id: 'Morning', label: 'Pre-Gen (AM)', time: '05:00-09:00', icon: Sunrise, color: 'bg-orange-50 text-orange-600 border-orange-200' },
                                          { id: 'Standard', label: 'Standard (Day)', time: '09:00-18:00', icon: Sun, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
                                          { id: 'Evening', label: 'Post-Gen (PM)', time: '18:00-22:00', icon: Sunset, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
                                        ].map((shift) => {
                                          const isSelected = editForm.executionPhase === shift.id;
                                          return (
                                            <button
                                              key={shift.id}
                                              type="button"
                                              onClick={() => {
                                                setEditForm(prev => prev ? {
                                                  ...prev,
                                                  executionPhase: shift.id,
                                                  executionTime: shift.time,
                                                } : null);
                                              }}
                                              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm border-2 transition-all ${isSelected
                                                ? `${shift.color} border-current shadow-md`
                                                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                }`}
                                            >
                                              <shift.icon className="w-4 h-4" />
                                              <span className="font-medium">{shift.label}</span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {editForm.executionTime && (
                                        <p className="mt-2 text-xs text-gray-500">
                                          Execution Time: <span className="font-medium text-gray-700">{editForm.executionTime}</span>
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Image Required */}
                                  <div className="grid grid-cols-12 gap-3 mt-3">
                                    <div className="col-span-12">
                                      <label className="inline-flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={editForm.isImageRequired || false}
                                          onChange={(e) => setEditForm(prev => prev ? { ...prev, isImageRequired: e.target.checked } : null)}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Image Required</span>
                                      </label>
                                    </div>
                                  </div>

                                  {/* Action Buttons */}
                                  <div className="flex gap-3 pt-2">
                                    <button
                                      onClick={() => handleSaveEdit(checkpointId)}
                                      className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                                      disabled={!editForm.name || !editForm.frequency}
                                    >
                                      Save Changes
                                    </button>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="-mx-6 mt-6 px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {showInactive
              ? `${checkpoints.length} total checkpoints`
              : `${checkpoints.filter((cp) => cp.status === "active").length} active checkpoints`
            }
          </span>
          <span>
            Changes will regenerate tasks for all equipment using this asset
          </span>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 z-[1001] overflow-y-auto bg-gray-500/50">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white rounded-lg border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border bg-red-50 border-red-200">
                    <XCircle size={20} className="text-red-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Deactivate Project Asset
                    </h2>
                    <p className="text-sm text-gray-600">
                      {assetName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsConfirmOpen(false)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-200 transition-all duration-200"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200">
                    <AlertTriangle size={14} className="text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                      Are you sure you want to{" "}
                      <span className="font-medium text-gray-900">
                        deactivate
                      </span>{" "}
                      <span className="font-medium text-green-600">{assetName}</span>?
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 leading-relaxed">
                        This will affect all associated equipment and tasks. The asset will no longer be available for new assignments.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                <button
                  onClick={() => setIsConfirmOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateProjectAssetStatus("inactive")}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 focus:ring-red-500 border border-transparent rounded-md transition-all duration-200 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Deactivate</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </RightDrawer>
  );
}
